
/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { concatMap, first } from 'rxjs/operators';
import { Field } from '@src/app/shared/field/Field';
import { Schedule } from '@src/app/model/schedule';
import { ScheduleCriteria } from '@src/app/model/schedule-criteria';
import { Unit } from '@src/app/model/unit';
import { UnitType } from '@src/app/model/unit-type';
import { AbstractPageComponent } from '@src/app/shared/form/abstract-page.component';
import { FormCheckboxComponent } from '@src/app/shared/form/form-checkbox/form-checkbox.component';
import { FormNumberComponent } from '@src/app/shared/form/form-number/form-number.component';
import { AppPicklistControl, FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { FormConfig } from "@src/app/shared/form/FormConfig";
import { GridField } from '@src/app/shared/grid/grid-field';
import { maxChars, required } from '@src/app/shared/validators';
import { UnitCategoryService } from '@src/app/modules/unit/unit-type-page/unit-category.service';
import { UnitTypeService } from '@src/app/modules/unit/unit-type-page/unit-type.service';
import { UnitService } from '@src/app/modules/unit/unit.service';
import { ActionHandler } from '@src/app/modules/budget/schedule-action-handler';
import { ScheduleService } from '@src/app/modules/budget/schedule.service';
import { ScheduleValidateAction } from '@src/app/modules/budget/schedule-validate-action';
import { ChangeChargeTypeAction } from '@src/app/modules/budget/schedule-charge-type-action';
import { EditPortionsAction } from '@src/app/modules/budget/schedule-edit-portions-action';
import { Frequency } from '@src/app/model/frequency';
import { AppFormControl } from '@src/app/shared/form/app-form-control';
import { FieldSet } from '@src/app/shared/form/field-set/field-set.component';
import { BCodeService } from '@src/app/modules/budget/bcode.service';
import { HttpParams } from '@angular/common/http';
import { BCode } from '@src/app/model/bcode';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { FormPageComponent } from '@src/app/shared/form/form-page/form-page.component';
import { FormNavLinkComponent } from '@src/app/shared/form/form-nav-button/form-nav-link.component';
import { makeIsCurrentPillField } from '@src/app/shared/form/form-pill/isCurrentPillField';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { SchedulePageNavRoute } from '@src/app/routes/schedule-page-nav';
import { UnitPageNavRoute } from '@src/app/routes/unit-page';
import { PersonPageNavRoute } from '@src/app/routes/person-page-nav';

@Component({
    selector: 'app-schedule-page',
    templateUrl: './schedule-page.component.html',
    styleUrls: ['./schedule-page.component.scss'],
    imports: [FormPageComponent]
})
export class SchedulePageComponent extends AbstractPageComponent {
    dataSvc = inject(ScheduleService);
    typeService = inject(UnitTypeService);
    categoriesService = inject(UnitCategoryService);
    private cds = inject(ConfirmDialogService);
    private unitService = inject(UnitService);
    private bCodeSvc = inject(BCodeService);


    actionHandler = new ActionHandler();

    chargeTypeField = FormPicklistComponent.make('Charge Type', 'chargeTypeId', null,
        { items: Schedule.chargeTypes },
        { valueChanges: (chargeTypeId: number) => this.chargeTypeChanged(chargeTypeId), validators: [required] }
    );

    chargeTypeAction = new ChangeChargeTypeAction(this.chargeTypeField);
    editPortionsAction = new EditPortionsAction(() => this.enablePortionEditing());
    validateAction = new ScheduleValidateAction();

    criteriaGrid: GridField = new GridField({
        field: { label: 'Criteria', value: 'criteria', formRow: 2 },
        rowFactory: this.criteriaRowFactory.bind(this),
        objFactory: () => of(new ScheduleCriteria()),
    });

    portionGrid = new GridField({
        // Read only and calculated by the server, so nothing worth posting. Turned back on
        // wherever the portions become editable - see chargeTypeChanged and EditPortionsAction.
        field: { label: 'Unit Apportionment', value: 'portions', formRow: 2, sendServer: false },
        rowFactory: () => [
            FieldMaker.id({ visible: Field.showAll }),
            FormTextComponent.make('unitId', 'unit.id', { name: 'unitId', visible: Field.noShow }),
            FormTextComponent.make('Unit Object', 'unit', { sendServer: false, visible: Field.noShow }),
            FormNavLinkComponent.makeDetail('Unit', 'unit.name', 'unitId', UnitPageNavRoute),
            FormTextComponent.make('Type', 'unit.type.name', { sendServer: false, disable: true }),
            FormTextComponent.make('Category', 'unit.category.name', { sendServer: false, disable: true }),
            FormNavLinkComponent.makeDetail('Owner', 'unit.owner.fullName', 'unit.owner.id', PersonPageNavRoute),
            FormNumberComponent.make('Portion', 'portion', {}, {
                disable: true,
                cellOpts: { width: '80px', style: 'text-align: center' },
                valueChanges: this.actionHandler.needsToBeApplied.bind(this.actionHandler)
            }),
            FormNumberComponent.make('Percent', 'percent',
                { format: 'percent', formatParms: '2.3-3' },
                { disable: true, cellOpts: { width: '80px', style: 'text-align: center' } },
            ),
        ],
        objFactory: () => of(new ScheduleCriteria()),
    });

    isCurrentField = makeIsCurrentPillField({
        toggle: (o: Schedule) => this.confirmCurrentChange(o),
        afterToggle: (o) => this.page.form.reloadContent(o),
        formColumn: 3,
    });

    fields: Field[] = [
        FieldMaker.id(),
        FieldMaker.rev(),
        FormTextComponent.make('Name', 'name', { validators: [required, maxChars(20)] }),

        this.chargeTypeField,

        FormPicklistComponent.make('Income Account', 'incomeBCodeId', 'incomeBCode',
            { service: this.bCodeSvc, serviceFilter: new HttpParams().set('typeId', BCode.TYPE.INCOME.id) },
            { formColumn: 1, validators: [required] }
        ),

        FormPicklistComponent.make('Billing Frequency', 'frequencyId', 'frequency',
            { items: [Frequency.YEARLY], optionDisplayValue: o => o.name + ' (' + o.perAnnum + ')' },
            { formColumn: 2, validators: [required] }
        ),

        FormCheckboxComponent.make('Sinking Fund', 'sinkingFund', { formColumn: 2 }),

        FormPicklistComponent.make('Prepayments Account', 'prepaidBCodeId', 'prepaidBCode',
            { service: this.bCodeSvc, serviceFilter: new HttpParams().set('typeId', BCode.TYPE.LIABILITY.id) },
            { formColumn: 2, validators: [required] }
        ),
        this.isCurrentField,
        FormTextComponent.make('Total Unit matches', '', { name: 'totalUnitCount', formColumn: 3, disable: true, visible: Field.formOnly }),
        FormTextComponent.make('Total Portions', 'totalPortionCount', { formColumn: 3, disable: true, visible: Field.formOnly }),

        FieldMaker.notes({ formColumn: 4 }),
    ];

    config = new FormConfig({
        navRoute: SchedulePageNavRoute,
        docPath: 'omc-books/budgeting/schedules',
        title: $localize`Schedule`,
        help: $localize`Schedules determine how costs will be allocated to unit owners`,
        fieldSet: new FieldSet({
            fields: [
                ...this.fields,
                this.criteriaGrid,
                this.portionGrid,
            ],
            formValidator: this.actionHandler.validate.bind(this.actionHandler),
            valueChanges: this.actionHandler.applyIfNeeded.bind(this.actionHandler),
            formLayout: [{ cells: [{ width: '30%' }, { width: '20%' }, { width: '20%' }, { width: '30%' }] },
            { cells: [{ colspan: 4, pageTab: 'yes' }] }],
        }),
        service: this.dataSvc,
        mode: 'list',
        beforeSave: this.actionHandler.beforeSave.bind(this.actionHandler),
        afterSave: (formGroup: UntypedFormGroup) => this.afterSave(formGroup),
        // A new schedule picks its charge type freely; a saved one has it locked, see beforeEdit.
        objectFactory: () => {
            this.lockChargeType(false);
            const s = new Schedule();
            s.chargeTypeId = Schedule.chargeId.fixed;
            return of(s);
        },
        beforeEdit: (o: Schedule) => {
            this.lockChargeType(true);
            return o;
        },

        // actions: [new FormActionButton({ name: 'Apply', icon: 'approval', action: this.actionHandler.calculate.bind(this.actionHandler) })],
        actions: [this.chargeTypeAction, this.editPortionsAction, this.validateAction],
        initNotification: new Subject<UntypedFormGroup>(),
    });

    private confirmCurrentChange(o: Schedule): Observable<Schedule | null> {
        const title = o.isCurrent ? $localize `Archive Schedule ${o.name}` : $localize `Reactivate schedule ${o.name}`;
        const msg = o.isCurrent
            ? $localize `Mark this schedule as archived, you no longer bill this schedule?`
            : $localize `Unarchive this schedule so that it is visible and can be used again ?`;
        return this.cds.openConfirm(title, msg).pipe(concatMap(answer => answer
            ? this.dataSvc.put({ id: o.id, revision: o.revision, isCurrent: !o.isCurrent })
            : of(null)));
    }

    constructor() {
        super();

        forkJoin({
            units: this.unitService.get().pipe(first()),
            unitTypes: this.typeService.get().pipe(first()),
            formGroup: this.config.initNotification.pipe(first()),
        }).subscribe((val) => {
            this.actionHandler.units = val.units as Unit[];
            this.actionHandler.unitTypes = val.unitTypes as UnitType[];
            this.actionHandler.formGroup = val.formGroup;
            this.actionHandler.portionMap();
            this.actionHandler.enableCriteriaPortions(val.formGroup.get('chargeTypeId').value);
            this.actionHandler.calculate();
        });
    }

    /** Guarded because beforeEdit and objectFactory can both run before the control exists -
     * AppFormControl reads field.disable when it is built, so setting it is enough on that path.
     */
    lockChargeType(locked: boolean) {
        this.chargeTypeField.disable = locked;
        if (!this.chargeTypeField.control) {
            return;
        }
        if (locked) {
            this.chargeTypeField.control.disable({ emitEvent: false });
        } else {
            this.chargeTypeField.control.enable({ emitEvent: false });
        }
    }

    /** changeChargeType makes the portion inputs editable on a custom schedule, so what we post
     * has to follow it - otherwise hand entered portions are silently dropped on save.
     */
    chargeTypeChanged(chargeTypeId: number) {
        this.actionHandler.changeChargeType(chargeTypeId);
        this.portionGrid.sendServer = chargeTypeId === Schedule.chargeId.custom;
    }

    /** The way in for a saved custom schedule, where the change event never fires on load. */
    enablePortionEditing() {
        this.portionGrid.sendServer = true;
        this.actionHandler.enablePortionPortions(Schedule.chargeId.custom);
    }

    /** A save can leave the apportionment behind - the criteria may now match different units.
     * Ask the server, and put the delta in front of the user rather than leaving it stale.
     */
    afterSave(formGroup: UntypedFormGroup) {
        this.actionHandler.afterSave(formGroup);

        const schedule = formGroup.getRawValue() as Schedule;
        if (!schedule?.id) {
            return;
        }
        this.dataSvc.validateSchedule(schedule.id).subscribe(validation => {
            if (validation && !validation.isValid) {
                this.validateAction.setup(schedule);
                this.validateAction.action().subscribe();
            }
        });
    }

    criteriaRowFactory() {
        return [
            FieldMaker.id(),

            FormPicklistComponent.make('Type', 'typeId', 'type',
                { service: this.typeService, refreshes: ['categoryId'] },
                { valueChanges: this.actionHandler.needsToBeApplied.bind(this.actionHandler) }
            ),

            FormPicklistComponent.make('Category', 'categoryId', 'category', { service: this.categoriesService },
                {
                    refresh: (o: UnitType, control: AppPicklistControl) => { if (o) { control.field.setPicklistItems(o.categories); control.setValue(null); } },
                    valueChanges: this.actionHandler.needsToBeApplied.bind(this.actionHandler),
                }),

            FormCheckboxComponent.make('Include', 'include', { valueChanges: this.actionHandler.needsToBeApplied.bind(this.actionHandler) }),

            FormNumberComponent.make('Portion Per Unit', 'portion', {}, {
                cellOpts: { width: '80px' },
                disable: this.chargeTypeField.control?.value === Schedule.chargeId.byType ? false : true,
                valueChanges: this.actionHandler.needsToBeApplied.bind(this.actionHandler)
            }),

            FormNumberComponent.make('Unit matches', '', {}, { cellOpts: { width: '80px' }, name: 'unitCount', disable: true }),
            FormNumberComponent.make('Total Portions', 'portionCount', {}, { cellOpts: { width: '80px' }, disable: true }),
            FieldMaker.deleteGridRow({
                clickMethod: function (ctl: AppFormControl) {
                    ctl.getRow().delete.bind(ctl.getRow())();
                    this.needsToBeApplied(true);
                    this.calculate(this.formGroup.value);
                }.bind(this.actionHandler)
            }),
        ];
    }
}
